/** @format */
import {
	AppProductReviewState,
	cleanReviewFilters,
	cleanSelectedTag,
	closeProductReviewDrawer,
	ComponentHandlerService,
	InvalidResponse,
	openProductReviewDrawer,
	ProductReviewAnalyticsService,
	ProductReviewDrawerType,
	removeSearchFilter,
	removeSortFilter,
	ReviewSummary,
	selectProductReviewDrawer,
	selectProductReviewSummary,
	selectProductStatistics,
	setReviewOnDrawer
} from '@product-reviews/data-access';
import {
	BVEventObject,
	BVEventObjectName,
	BVEventObjectType,
	BVEventType,
	BVProduct,
	ReviewContentListAction,
	ReviewContentListConfiguration
} from '../models';
import { CommonModule, DOCUMENT } from '@angular/common';
import {
	Component,
	ComponentRef,
	inject,
	input,
	OnDestroy,
	OnInit,
	Renderer2,
	ViewChild,
	ViewContainerRef
} from '@angular/core';
import {
	homefieldIconChevronRight,
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { filter, takeUntil } from 'rxjs/operators';

import { Chain } from '@shared/data-access/interfaces';
import { ReviewButtonComponent } from '../review-button/review-button.component';
import { ReviewInformationDrawerComponent } from '../review-information-drawer/review-information-drawer.component';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { UtilityService } from '@shared/data-access/services';

@Component({
	selector: 'product-reviews-content-list',
	templateUrl: './review-content-list.component.html',
	styleUrl: './review-content-list.component.scss',
	imports: [HomefieldIconLibraryModule, ReviewButtonComponent, CommonModule]
})
export class ReviewContentListComponent implements OnInit, OnDestroy {
	private readonly store = inject<Store<AppProductReviewState>>(Store);
	private readonly utilityService = inject(UtilityService);
	private readonly analyticsService = inject(ProductReviewAnalyticsService);
	private readonly renderer2 = inject(Renderer2);
	private readonly document = inject<Document>(DOCUMENT);
	private readonly componentHandlerService = inject(ComponentHandlerService);
	private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);
	private readonly chain = inject(Chain);

	readonly ratingCount = input<number | null>(null);
	readonly ratingValue = input<number | null>(null);

	/**
	 * @description
	 * isUtagBazaarvoiceEnabled - Flag to enable Bazaar Voice script loading using UTAG
	 */
	readonly isUtagBazaarvoiceEnabled = input(false);

	createdComponent: ComponentRef<ReviewInformationDrawerComponent> | null = null;

	@ViewChild('drawerContainer', { static: true, read: ViewContainerRef })
	drawerContainer: ViewContainerRef;
	storeSelector$ = this.store.select(selectProductReviewSummary);
	storeSelectorStatistics$ = this.store.select(selectProductStatistics);
	storeSelectorDrawer$ = this.store.select(selectProductReviewDrawer);
	drawerStatus$ = this.store.select(selectProductReviewDrawer);
	private UNSUBSCRIBE$ = new Subject<void>();

	flags = {
		hasReviewSummary: false,
		hasStatistics: false
	};

	showSidePanel = false;
	drawerTitle = '';
	listConfiguration: ReviewContentListConfiguration[] = [];
	reviewSummary: ReviewSummary;
	shouldRenderType: ProductReviewDrawerType = ProductReviewDrawerType.NONE;
	firstFocusable: HTMLElement;
	lastFocusable: HTMLElement;
	isFirstPanelOpen: boolean;

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([homefieldIconChevronRight]);
	}

	/* Life-cycle hooks */
	ngOnInit() {
		this.setupStore();
		this.setupExternalDrawerEvent();
	}

	ngOnDestroy() {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	/* Public API */
	onClosePanelEvent() {
		this.drawerTitle = '';
		this.handleDrawerEvent();
		this.cleanDrawerStore();
	}

	/* Helper methods */

	/**
	 * This method subscribe to the store selected in the constructor
	 */
	setupStore() {
		this.storeSelector$.pipe(takeUntil(this.UNSUBSCRIBE$)).subscribe((reviewSummaryData) => {
			if (!(reviewSummaryData instanceof InvalidResponse)) {
				if (!this.flags.hasReviewSummary && !!reviewSummaryData) {
					this.flags.hasReviewSummary = true;
					this.reviewSummary = reviewSummaryData;

					if (!isNaN(reviewSummaryData.reviewCount)) {
						this.setReviewConfiguration(reviewSummaryData.reviewCount);
					}
				}

				this.sortListConfiguration();
			}
		});

		this.storeSelectorStatistics$
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe((statisticsData) => {
				const isInvalidResponse = statisticsData instanceof InvalidResponse;

				if (isInvalidResponse) return;

				const hasQAConfiguration = this.listConfiguration.some(
					(config) => config.type === ProductReviewDrawerType.QUESTIONS
				);
				if (!hasQAConfiguration) {
					this.setQAConfiguration(statisticsData);
				}
			});
	}

	/**
	 * This method Handle the actions in the list
	 */
	setupConfiguration(configuration: Partial<ReviewContentListConfiguration>) {
		const config: ReviewContentListConfiguration = {
			callback: this.handleToggleReadAllReviewsEvent.bind(this),
			type: ProductReviewDrawerType.REVIEWS,
			...(configuration as ReviewContentListConfiguration)
		};

		this.listConfiguration = [...this.listConfiguration, config];
	}

	/**
	 * Open drawer based on data passed by store to display the review, then
	 * wait 2 second and go to the specify review using scroll to
	 */
	setupExternalDrawerEvent() {
		this.storeSelectorDrawer$
			.pipe(
				takeUntil(this.UNSUBSCRIBE$),
				filter((data) => !(data instanceof InvalidResponse))
			)
			.subscribe(({ drawerType, open }) => {
				this.showSidePanel = open;

				if (open) {
					if (drawerType === ProductReviewDrawerType.REVIEWS) {
						if (this.isFirstPanelOpen === undefined) {
							this.isFirstPanelOpen = true;
						} else {
							this.isFirstPanelOpen = false;
						}
					}
					this.setDrawerHeader(drawerType);
					this.shouldRenderType = drawerType;
					this.createComponent(drawerType);
				}
			});
	}

	/**
	 * This method set the action for Reviews
	 */
	setReviewConfiguration(reviewCount: number) {
		const title = `Read All Reviews (${this.formatNumberWithCommas(reviewCount)})`;
		let config: Partial<ReviewContentListConfiguration> = { title };

		if (!reviewCount) {
			config = {
				title: 'Reviews (0)',
				actions: [
					{
						title: 'Write A Review',
						callback: this.handleToggleReadAllReviewsEvent.bind(this)
					}
				]
			};
		}

		this.setupConfiguration(config);
	}

	/**
	 * This method set the action for QA
	 */
	setQAConfiguration(statistics: { totalAnswerCount: number; totalQuestionCount: number }) {
		const shouldShowQA = this.chain.chainMarketingIdentifierAbbr === 'dsg';
		if (shouldShowQA) {
			this.setupQAConfigurationUI(statistics);
		}
	}

	setupQAConfigurationUI(statistics: { totalAnswerCount: number; totalQuestionCount: number }) {
		const hasQuestions = statistics.totalQuestionCount && statistics.totalQuestionCount > 0;
		const hasAnswers = statistics.totalAnswerCount && statistics.totalAnswerCount > 0;
		const questionText =
			statistics.totalQuestionCount && statistics.totalQuestionCount === 1
				? 'Question'
				: 'Questions';
		const answerText =
			statistics.totalAnswerCount && statistics.totalAnswerCount === 1 ? 'Answer' : 'Answers';
		const totalQuestionCount = this.formatNumberWithCommas(statistics.totalQuestionCount);
		const totalAnswerCount = this.formatNumberWithCommas(statistics.totalAnswerCount);

		let subtitle = null;
		if (hasQuestions && hasAnswers) {
			subtitle = `(${totalQuestionCount} ${questionText}, ${totalAnswerCount} ${answerText})`;
		} else if (hasQuestions) {
			subtitle = `(${totalQuestionCount} ${questionText})`;
		}

		const config: Partial<ReviewContentListConfiguration> = {
			title: `Q&A`,
			type: ProductReviewDrawerType.QUESTIONS,
			subtitle,
			callback: this.handleToggleQAEvent.bind(this),
			...(!statistics.totalQuestionCount && {
				actions: [
					{ title: 'Ask A Question', callback: this.handleToggleQAEvent.bind(this) }
				]
			})
		};

		this.setupConfiguration(config);
	}

	/*
		Important information:
		At this moment we only handle `Reviews` and `QA` so the sort is alphabetical order because is not worth to handle validation by type.
		If in some future we handle more than two types we should have this on mind.
	 */
	sortListConfiguration() {
		this.listConfiguration.sort((configA, configB) => {
			if (configA.type > configB.type) {
				return -1;
			} else if (configA.type < configB.type) {
				return 1;
			} else {
				return 0;
			}
		});
	}

	/**
	 * This method Handle the actions in the reviews drawer (open/close), and which component should render in the body
	 */
	handleDrawerEvent({ type }: Partial<ReviewContentListConfiguration> = {}) {
		if (type) {
			this.setDrawerHeader(type);
			this.shouldRenderType = type;
		}

		const body = this.document.querySelector('body');
		if (this.showSidePanel) {
			this.cleanStore();
			// There's a component adding the overflow on load
			body.setAttribute('style', 'overflow:scroll');
			this.renderer2.removeClass(this.document.body, 'side-panel-open');
		} else {
			this.store.dispatch(openProductReviewDrawer({ drawerType: type }));
			this.renderer2.addClass(this.document.body, 'side-panel-open');
			// Symbolic timeout to wait for drawer-list to be ready
			setTimeout(() => {
				body.setAttribute('style', 'overflow:hidden');
			}, 100);
		}
	}

	/**
	 * Cleaning the store for filters, and tags after the drawer is closed
	 */
	cleanStore() {
		this.store.dispatch(closeProductReviewDrawer());
		this.store.dispatch(cleanReviewFilters());
		this.store.dispatch(removeSearchFilter());
		this.store.dispatch(cleanSelectedTag());
		this.store.dispatch(removeSortFilter());
	}

	handleToggleReadAllReviewsEvent(configuration?: ReviewContentListConfiguration) {
		this.handleDrawerEvent(configuration);
		this.analyticsService.fireReviewAnalytics<{ event: string; value: string }>({
			name: 'multiUseAnalyticsEvent',
			payload: { event: 'RatingsAndReviewsInteractions', value: 'ReadAllReviews' }
		});
		const sidePanelElement = this.document.getElementById('review-side-panel');
		if (sidePanelElement) {
			this.utilityService.scrollToElement(sidePanelElement);
		}
	}

	handleToggleQAEvent(configuration?: ReviewContentListConfiguration) {
		this.handleDrawerEvent(configuration);
		this.analyticsService.fireReviewAnalytics<{ event: string; value: string }>({
			name: 'multiUseAnalyticsEvent',
			payload: { event: 'RatingsAndReviewsInteractions', value: 'QADrawer' }
		});

		const bvObject: BVEventObject = {
			type: BVEventObjectType.USED,
			name: BVEventObjectName.LINK,
			brand: this.reviewSummary.brand,
			productId: '',
			bvProduct: BVProduct.RATINGSANDREVIEWS,
			categoryId: this.reviewSummary.category,
			detail1: 'q_and_a',
			detail2: 'PrimaryRatingSummary'
		};

		BV.pixel.trackEvent(BVEventType.FEATURE, bvObject);
	}

	/**
	 * Set drawer title based on the component to display
	 * At the moment we will handle only questions and answer, so if in some future we handel more than 3 change the logic to a switch/case
	 */
	setDrawerHeader(type: ProductReviewDrawerType) {
		if (type === ProductReviewDrawerType.REVIEWS) {
			this.drawerTitle = 'Reviews';
		} else if (type === ProductReviewDrawerType.QUESTIONS) {
			this.drawerTitle = 'Q&A';
		}
	}

	/**
	 * Clear current review on drawer store
	 */
	cleanDrawerStore() {
		this.store.dispatch(
			setReviewOnDrawer({
				currentReview: ''
			})
		);
	}

	/*
	 * Format numbers using commas
	 */
	formatNumberWithCommas(number: number): string {
		return new Intl.NumberFormat('en-US').format(number);
	}

	/*
	 * Track actions changed
	 */
	trackByAction(index: number, action: ReviewContentListAction) {
		return index.toString() + action.title;
	}

	/*
	 * Track configuration changed
	 */
	trackByConfiguration(index: number, configuration: ReviewContentListConfiguration) {
		return index.toString() + configuration.title;
	}

	private createComponent(drawerType: ProductReviewDrawerType): void {
		this.createdComponent = this.componentHandlerService.add(
			this.drawerContainer,
			ReviewInformationDrawerComponent,
			[
				{ key: 'reviewDrawerType', value: drawerType },
				{ key: 'drawerTitle', value: this.drawerTitle },
				{ key: 'isFirstPanelOpen', value: this.isFirstPanelOpen }
			]
		);

		// When we update to Angular v20, we should update this to make use of input binding instead so these inputs can be reactive.
		// https://github.com/angular/angular/pull/60137
		this.createdComponent.setInput('ratingCount', this.ratingCount());
		this.createdComponent.setInput('ratingValue', this.ratingValue());
	}
}
